// Graph.js
// @flow
import * as React from 'react';
import * as _ from 'lodash';
import safeUrl from '../util/safeUrl';
import { withRouter } from 'react-router';
import { observer } from 'mobx-react';
import { injectIntl } from 'react-intl';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';

import SinaPlot from '../SinaPlot.js';
import Modal from './Modal.js';

import type { AppStateType, DataType } from '../Types';

import helpers from '../util/helpers';

import "../css/Graph.css";

type Props = {
	appState: AppStateType;
	chemical_full: string,
	medium: string,
	chemicalGroup: string,
	pointerEvents: boolean,
	yLimits: number[],
	data: DataType[],
	chemical: string,
	noDetectText: string,
};

type State = {
	showParticipantModal: boolean,
	showBenchmarkModal: boolean;
	showChemicalModal: boolean,
	showOtherParticipantsModal: boolean,
	showNonDetectModal: boolean,
	participantModal: Object,
	chemicalModalMessageId: string,
};

const Graph = injectIntl(withRouter(observer(class Graph extends React.Component<Props, State> {
	sinaPlot: Object

	constructor(props) {
		super(props);
		this.benchmarks = this.getBenchmarks(props)
		this.onModalClose = this.onModalClose.bind(this)
		this.state = {
			showParticipantModal: false,
			showBenchmarkModal: false,
			showChemicalModal: false,
			showOtherParticipantsModal: false,
			showNonDetectModal: false,
			participantModal: {},
			chemicalModalMessageId: '',
			expanded: false,
		};
	}

	getData() {
		return this.props.data;
	}

	getParticipantData() {
		return _.filter(this.getData(), { mine: true });
	}

	getNonDetects() {
		return _.filter(this.getData(), { detected: false });
	}

	getBenchmarks(props) {
		if (!props) (props = this.props)
		let benchmarks = props.appState.benchmarks.slice();

		if(props.chemical_full !== undefined && props.chemical_full !== null) {
			benchmarks = _.filter(benchmarks, { chemical_full: props.chemical_full })
		}


		if(props.medium !== undefined && props.medium !== null) {
			benchmarks = _.filter(benchmarks, (d) => props.medium.toLowerCase() === d.medium.toLowerCase())
		}

		return benchmarks;
	}

	showVisitBadges() {
		return false;
		// return this.props.chemicalGroup !== 'Water CVOCs' && this.props.chemicalGroup !== 'Water Phthalates';
	}

	avatarName() {
		return this.props.appState.avatar || 'house'
		// return !this.showVisitBadges() ? 'house' : this.props.appState.avatar;
	}

	componentDidMount() {
		const avatar = this.avatarName();
		let units = this.getData()[0].unit

		this.sinaPlot = new SinaPlot(this.refs.graph, avatar);

		this.sinaPlot.useAvatars = this.props.appState.graphs.useAvatars;
		this.sinaPlot.useParticipantAvatar = this.props.appState.graphs.useParticipantAvatar;
		this.sinaPlot.jitter = this.props.appState.graphs.jitter;
		this.sinaPlot.sina = this.props.appState.graphs.sina;
		this.sinaPlot.jitterAmount = this.props.appState.graphs.jitterAmount;
		// this.sinaPlot.labelOffset = this.props.appState.graphs.labelOffset;
		this.sinaPlot.showVisitBadges = this.showVisitBadges();
		this.sinaPlot.units = units;

		this.sinaPlot.yLimits = this.props.yLimits;
		this.sinaPlot.noDetectText = this.props.intl.messages['nodetecttext_yaxis']; // this.props.noDetectText -- not needed
		this.sinaPlot.tutorialMode = this.props.tutorialMode || false;
		this.sinaPlot.tutorialSettings = this.props.tutorialSettings;
		this.sinaPlot.setup();
		this.sinaPlot.onChemicalClicked = this.onChemicalClicked.bind(this);
		this.sinaPlot.onParticipantClicked = this.onParticipantClicked.bind(this);
		this.sinaPlot.onBenchmarkClicked = this.onBenchmarkClicked.bind(this);
		this.sinaPlot.onNonDetectClicked = this.onNonDetectClicked.bind(this);
		this.sinaPlot.onOtherParticipantsClicked = this.onOtherParticipantsClicked.bind(this);
		this.sinaPlot.onAxisClicked = this.onAxisClicked.bind(this);

		this.sinaPlot.titleClickable = this.hasChemicalSpecificData();

		if(this.props.appState.data.length > 0) {
			this.sinaPlot.updateData(this.getData(), this.getBenchmarks(), this.props.appState.participantId);
		}
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		if(prevProps.chemical !== this.props.chemical || prevProps.chemicalGroup !== this.props.chemicalGroup) {
			this.sinaPlot.clear()
			this.sinaPlot.avatar = this.avatarName();
			this.sinaPlot.showVisitBadges = this.showVisitBadges();
			this.sinaPlot.yLimits = this.props.yLimits;
			this.sinaPlot.noDetectText = this.props.intl.messages['nodetecttext_yaxis']; // this.props.noDetectText -- not needed
			this.sinaPlot.titleClickable = this.hasChemicalSpecificData();
			this.sinaPlot.tutorialMode = this.props.tutorialMode || false;
			this.sinaPlot.tutorialSettings = this.props.tutorialSettings;
			this.sinaPlot.setup()
			if(this.props.appState.data.length > 0) {
				this.sinaPlot.updateData(this.getData(), this.getBenchmarks(), this.props.appState.participantId);
			}
		}
		if (prevState.expanded !== this.state.expanded) {
			this.sinaPlot.clear()
			this.sinaPlot.expanded = this.state.expanded
			this.sinaPlot.setup()
			if(this.props.appState.data.length > 0) {
				this.sinaPlot.updateData(this.getData(), this.getBenchmarks(), this.props.appState.participantId);
			}
		}
	}

	hasChemicalSpecificData() {
		return this.props.intl.messages[`chemicals.${this.props.chemicalGroup.toLowerCase().replace(' ', '_')}.moreinfo.${this.props.chemical}`] !== undefined;
	}

	onParticipantClicked(d) {
		if (this.state.expanded) {
			console.log("onParticipantClicked", this);
			helpers.eventLog("modal - showParticipantModal");
			this.setState({
				showParticipantModal: true,
				showBenchmarkModal: false,
				showNonDetectModal: false,
				showOtherParticipantsModal: false,
				showChemicalModal: false,
				participantModal: d
			});
			document.getElementsByTagName("body")[0].style.overflow = "hidden";
		}
	}

	onBenchmarkClicked() {
		if (this.state.expanded) {
			console.log("benchmarkClicked", this);
			helpers.eventLog("modal - showBenchmarkModal");
			this.setState({
				showBenchmarkModal: true,
				showParticipantModal: false,
				showNonDetectModal: false,
				showOtherParticipantsModal: false,
				showChemicalModal: false,
			});
			document.getElementsByTagName("body")[0].style.overflow = "hidden";
		}
	}

	onChemicalClicked(chemical) {
		if (this.state.expanded) {
			helpers.eventLog("modal - showChemicalModal - chem:" + chemical);
			this.setState({
				showChemicalModal: true,
				showParticipantModal: false,
				showBenchmarkModal: false,
				showNonDetectModal: false,
				showOtherParticipantsModal: false,
				chemicalModalMessageId: chemical,
			});
			document.getElementsByTagName("body")[0].style.overflow = "hidden";
		}
	}

	onNonDetectClicked() {
		if (this.state.expanded) {
			console.log("onNonDetectClicked", this);
			helpers.eventLog("modal - showNonDetectModal");
			this.setState({
				showNonDetectModal: true,
				showParticipantModal: false,
				showBenchmarkModal: false,
				showOtherParticipantsModal: false,
				showChemicalModal: false,
			});
			document.getElementsByTagName("body")[0].style.overflow = "hidden";
		}
	}

	onOtherParticipantsClicked(d) {
		if (this.state.expanded) {
			console.log("showOtherParticipantsModal", this);
			helpers.eventLog("modal - showOtherParticipantsModal");
			this.setState({
				showOtherParticipantsModal: true,
				showParticipantModal: false,
				showBenchmarkModal: false,
				showNonDetectModal: false,
				showChemicalModal: false,
			});

			document.getElementsByTagName("body")[0].style.overflow = "hidden";
		}
	}

	onAxisClicked() {

	}

	componentWillUpdate() {
		//this.sinaPlot.updateData(this.getData(), this.getBenchmarks(), this.props.appState.participantId);
	}

	onModalClose(e) {
		console.log("graph - close modal");
		this.setState({
			showParticipantModal: false,
			showBenchmarkModal: false,
			showNonDetectModal: false,
			showOtherParticipantsModal: false,
			showChemicalModal: false
		});
		document.getElementsByTagName("body")[0].style.overflow = "visible";
	}

	toggleExpanded() {
		if (this.state.expanded) {
			helpers.eventLog(`graph - collapse graph - ${this.props.chemical}`);
		} else {
			helpers.eventLog(`graph - expand graph - ${this.props.chemical}`);
		}

		let newToggle = !this.state.expanded;
		if (this.props.tutorialMode && this.props.tutorialSettings.clickExpandedGraph) {
			this.props.tutorialSettings.clickExpandedGraph(newToggle)
		}
		if (this.props.tutorialMode && this.props.tutorialSettings.noToggle) {
			// do nothing
		} else {
			this.setState({
				expanded: newToggle,
			})
		}
	}

	render() {
		if(this.getParticipantData().length === 0) {
			return <div></div>;
		}

		// let participantModal;
		// let benchmarkModal;
		// let nonDetectModal;
		// let otherParticipantsModal;
		// let chemicalModal;
		// if (this.state.showParticipantModal){
		// 	participantModal = (<ParticipantModal
		// 		// appState={this.props.appState}
		// 		data={this.state.participantModal}
		// 		showVisitBadge={this.showVisitBadges()}
		// 		avatarName={this.avatarName()}
		// 		onClose={this.onModalClose} />)
		// } else if (this.state.showBenchmarkModal){
		// 		benchmarkModal = (<BenchmarkModal
		// 			// appState={this.props.appState}
		// 			data={this.benchmarks}
		// 			onClose={this.onModalClose} />)
		// } else if (this.state.showNonDetectModal) {
		// 		nonDetectModal = <NonDetectModal
		// 			appState={this.props.appState}
		// 			data={this.getNonDetects()}
		// 			allData={this.getData()}
		// 			onClose={this.onModalClose} />
		// } else if (this.state.showOtherParticipantsModal) {
		// 		otherParticipantsModal = <OtherParticipantsModal
		// 			appState={this.props.appState}
		// 			data={this.getData()}
		// 			onClose={this.onModalClose} />
		// } else if (this.state.showChemicalModal) {
		// 	chemicalModal = <ChemicalModal
		// 		appState={this.props.appState}
		// 		chemicalGroup={this.props.chemicalGroup}
		// 		chemical={this.state.chemicalModalMessageId}
		// 		onClose={this.onModalClose}
		// 		/>
		// }

		const className = 'graph ' + (this.props.pointerEvents === false ? 'no-pointer-events' : '');

		let chevronState;
		if (this.state.expanded) {
			chevronState = "lighter chevron-arrow-down"
		} else {
			chevronState = "lighter chevron-arrow-right"
		}
		let chevron;
		if (this.props.tutorialMode && this.props.tutorialSettings.noToggle) {
			// don't have the chevron to toggle
		} else {
			chevron = <span className={chevronState}></span>
		}

		let learnMoreChemical;
		if (this.state.expanded && this.hasChemicalSpecificData()) {
			learnMoreChemical = (
				<div
					className="learn-more-about-chemical"
					onClick={() => this.onChemicalClicked(this.props.chemical)}
				>
					<FormattedMessage id='learn_more_about_this_chemical' />
				</div>
			)
		}

		// {participantModal}
		// {benchmarkModal}
		// {nonDetectModal}
		// {otherParticipantsModal}
		// {chemicalModal}


		return (
			<div {...(this.state.expanded ? {} : {onClick: this.toggleExpanded.bind(this)})}>
				<h3
					className='graphs-chemical-name'
					{...(this.state.expanded ? {onClick: this.toggleExpanded.bind(this)} : {})}
				>
						{chevron}
						{this.props.chemical}
				</h3>
				{learnMoreChemical}
				<div className={className} ref='graph'>

					{!this.state.showParticipantModal
		        ? null
		        : <ParticipantModal
							data={this.state.participantModal}
							showVisitBadge={this.showVisitBadges()}
							avatarName={this.avatarName()}
							onClose={this.onModalClose} />
		      }

					{!this.state.showBenchmarkModal
		        ? null
		        : <BenchmarkModal
							data={this.benchmarks}
							onClose={this.onModalClose} />
		      }

					{!this.state.showNonDetectModal
		        ? null
		        : <NonDetectModal
							appState={this.props.appState}
							data={this.getNonDetects()}
							allData={this.getData()}
							onClose={this.onModalClose} />
		      }

					{!this.state.showOtherParticipantsModal
		        ? null
		        : <OtherParticipantsModal
							appState={this.props.appState}
							data={this.getData()}
							onClose={this.onModalClose} />
		      }

					{!this.state.showChemicalModal
		        ? null
		        : <ChemicalModal
							appState={this.props.appState}
							chemicalGroup={this.props.chemicalGroup}
							chemical={this.state.chemicalModalMessageId}
							onClose={this.onModalClose}
							/>
		      }




				</div>
				{this.state.expanded &&
					<p className="tap-graph-message"><FormattedMessage id="click_to_learn_more" /></p>
				}
			</div>
		);
	}
})))

// type ParticipantModalProps = { data: Object, onClose: () => void, appState: Object, showVisitBadge: boolean, avatarName: string };
class ParticipantModal extends React.Component {

	shouldComponentUpdate() {
		console.log("participant modal - should component update");
		return false;
	}

	render() {
		console.log("render participant modal");

		const nobr = { whiteSpace: 'nowrap' };

		const visitText = (isNaN(this.props.data.visit) || this.props.data.visit === 0) ? null : (<span style={nobr}> <FormattedMessage id='your_level_in_visit' defaultMessage='en la visita' /> {this.props.data.visit}</span>);

		let units = this.props.data.unit

		const content = this.props.data.detected ?
				(<span><FormattedMessage id='your_level' values={{chemical: this.props.data.chemical, value: this.props.data.value, units: units }} /> {visitText}</span>)
			:
				(<span><FormattedMessage id='your_level_not_detected' values={{ chemical: this.props.data.chemical }} /> {visitText}</span>);

		let visit;
		if (this.props.data.visit) {
			visit = (<div>{this.props.data.visit}</div>)
		}

		return (
			<Modal onClose={this.props.onClose} className='participant'>
				<div className='participant'>
					<img src={safeUrl(`/images/${this.props.avatarName}.png`)} width='100' alt='Your Avatar' />
					{visit}
				</div>
				<h1><FormattedMessage id='your_results' defaultMessage='Sus resultados' /> {this.props.data.chemical}</h1>

				{content}

			</Modal>
		);
	}
}

// type BenchmarkModalProps = {data: Object[], onClose: () => void };
class BenchmarkModal extends React.Component {

	shouldComponentUpdate(nextProps, nextState) {
		console.log("benchmark modal - should component update");
		return false;
	}

	render() {
		console.log("benchmark modal - render");

		let h1name = this.props.data[0].name;

		// XXX: compounds.json name and description is normally hardwired in spanish.  Except see DINP
		// https://trello.com/c/zlQfWcJc/23-us-median-needs-translation-when-clicking-legend
		let usingspanish = 0; // should not happen now ... see GLOBAL_locale
		if (usingspanish)
		{
			if (h1name.match(/US Median/i)) h1name = "Media EEUU...";
		}

		//XXX: missing description in DINP in compounds.json from api
		let thedesc = this.props.data[0].description;
		if (!thedesc) thedesc = "[Error: no detalles]";
		let isStar =  (this.props.data[0].symbol === 'star'); // props.data[0] -> chemical, chemical_full, description, detected, medium, name, symbol, value,
		var  svg = <img src={safeUrl(`/images/usflag.svg`)} width='100' alt='{h1name}' />;
		if (isStar)
		{
			svg = <img src={safeUrl(`/images/graph-blue-star.png`)} width='100' alt='{h1name}' />;
		}

		return (
			<Modal onClose={this.props.onClose}>
				{svg}
				<h1>{h1name}</h1>
				{thedesc}
			</Modal>
		)
	}
}

type NonDetectModalProps = { appState: Object, data: Object[], allData: Object[], onClose: () => void };
class NonDetectModal extends React.Component<NonDetectModalProps> {
	render() {
		const avatar = this.props.appState.avatar === 'face-1' ? 'face-2' : 'face-1';
		const chemical = this.props.allData[0].chemical;
		const numNonDetects = this.props.data.length;
		let percentage = Math.round(this.props.data.length / this.props.allData.length * 100);
		if (percentage < 1) percentage = <FormattedMessage id='non_detects_less_than_1' />;
		if (percentage == 100 && this.props.data.length < this.props.allData.length) {percentage = 99}

		return (
			<Modal onClose={this.props.onClose}>
				<img src={safeUrl(`/images/${avatar}.png`)} width='100' alt='Non-detect values' />
				<h1><FormattedMessage id='not_detected' defaultMessage='No se detectado' /></h1>
				<FormattedMessage id='non_detects' values={{ value: numNonDetects, percent: percentage, chemical: chemical }} />
			</Modal>
		)
	}
}

type OtherParticipantsModalProps = { appState: Object, data: Object[], onClose: () => void };
class OtherParticipantsModal extends React.Component<OtherParticipantsModalProps> {
	render() {
		const avatar = this.props.appState.avatar === 'face-1' ? 'face-2' : 'face-1';
		const data = this.props.data;
		const numParticipants = _.chain(this.props.data).map('recruit_identifier').uniq().value().length;
		const numSamples = _.filter(this.props.data, (d) => d.mine === false).length

		return (
			<Modal onClose={this.props.onClose}>
				<img src={safeUrl(`/images/${avatar}.png`)} width='100' alt='Other Participants' />
				<h1><FormattedMessage id='other_participants' /></h1>
				<FormattedMessage id='other_participants_content' values={{ number: numParticipants - 1, chemical: data[0].chemical, samples: numSamples }} />
			</Modal>
		)
	}
}

type ChemicalModalProps = { chemicalGroup: string, chemical: string, onClose: () => void };
class ChemicalModal extends React.Component<ChemicalModalProps> {
	render() {
		const group_name = helpers.convertGroupName(this.props.chemicalGroup); // this.props.chemicalGroup.toLowerCase().replace(' ', '_')
		const messageId = `chemicals.${group_name}.moreinfo.${this.props.chemical}`;
		return (
			<Modal onClose={this.props.onClose} className='more-info left-align' fullScreen={true}>
				<FormattedHTMLMessage id={messageId} />
			</Modal>
		);
	}
}

export default Graph;



// WEBPACK FOOTER //
// ./src/components/Graph.js